import { useRef, useState } from "react";
import { Check, X, Upload, Download, FileSpreadsheet, ChevronDown, ChevronRight, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, formatApiError, fmtInt, tokenStore } from "@/lib/api";
import { MoneyInput } from "@/components/MoneyInput";
import { downloadXlsxFrom, rowToPayload } from "@/components/HistoricalsMatrix";

// Kasstroom-opbouw per boekjaar (indirecte methode). `field` = veld op de historische rij; `computed`
// rijen volgen uit de resultatenrekening en zijn alleen-lezen. Alle bedragen gesigneerd: + = kastoename.
const num = (v) => Number(v) || 0;
export const LIQ_ROWS = [
  { key: "liquide_01_jan", label: "Liquide middelen 1 januari", editable: true, opening: true },
  { key: "bedrijfsresultaat", label: "Exploitatieresultaat (inkomsten − kosten − afschrijving)", computed: (r) => num(r.revenue) + num(r.other_income) - num(r.operating_costs) - num(r.depreciation) },
  { key: "rente", label: "Rente (betaald − ontvangen)", computed: (r) => -num(r.interest_paid) + num(r.interest_income) },
  { key: "afschrijving", label: "Afschrijving (geen kasuitgave, terugtellen)", computed: (r) => num(r.depreciation) },
  { key: "afschr_invest_next_years", label: "Afschrijving investeringen volgende boekjaren", editable: true },
  { key: "dotaties_voorzieningen", label: "Dotaties voorzieningen", editable: true },
  { key: "onttrekkingen_voorzieningen", label: "Onttrekkingen uit voorzieningen (groot onderhoud)", editable: true },
  { key: "bestemmingsreserves_resultaat", label: "Aan resultaat toegevoegde bestemmingsreserves", editable: true },
  { key: "mutatie_werkkapitaal", label: "Verandering in werkkapitaal", editable: true },
  { key: "investeringen_materiele_activa", label: "Investeringen in materiële activa (−)", editable: true },
  { key: "verhoging_kortlopende_schulden", label: "Verhoging kortlopende schulden", editable: true },
  { key: "verhoging_langlopende_schulden", label: "Verhoging langlopende schulden", editable: true },
  { key: "mutatie_debiteuren_crediteuren", label: "Mutaties debiteuren / crediteuren", editable: true },
  { key: "btw_afdracht_teruggave", label: "BTW afdracht (−) / teruggave (+)", editable: true },
];

export const openingFor = (rows, i) => (i === 0 ? num(rows[0]?.liquide_01_jan) : num(rows[i - 1]?.cash_bank_balance));
export const computeSaldo = (rows, i) => openingFor(rows, i) + LIQ_ROWS
  .filter((r) => !r.opening)
  .reduce((s, r) => s + (r.computed ? r.computed(rows[i]) : num(rows[i][r.key])), 0);

// In gebruik zodra minstens één invulbare kasstroomregel gevuld is (handmatig, Excel of AI-import).
// Zolang dat niet zo is, is elk jaar per definitie "niet aansluitend" — dan zwijgt het blok.
export const liquidityInUse = (historicals) => {
  const editable = LIQ_ROWS.filter((r) => r.editable || r.opening).map((r) => r.key);
  return (historicals || []).some((r) => editable.some((k) => num(r[k]) !== 0));
};

export default function LiquidityMatrix({ t, clubId, historicals, locked = false, onChange, defaultOpen = false }) {
  const rows = [...(historicals || [])].sort((a, b) => (a.year || 0) - (b.year || 0));
  const inUse = liquidityInUse(rows);
  const [open, setOpen] = useState(defaultOpen);
  const [revealed, setRevealed] = useState(false);
  const fileRef = useRef();
  if (rows.length === 0) return null;
  const mismatches = rows.filter((r, i) => Math.abs(Math.round(computeSaldo(rows, i) - num(r.cash_bank_balance))) > 1).length;

  const saveField = async (row, field, value) => {
    try {
      await api.put(`/historicals/${row.id}`, { ...rowToPayload(row), [field]: num(value) });
      toast.success(t("Opgeslagen"));
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || String(e.message || e));
    }
  };

  const uploadLiquidity = async (file) => {
    if (!file || !clubId) return;
    const form = new FormData();
    form.append("file", file);
    const token = tokenStore.get();
    try {
      const res = await fetch(`${API_BASE}/clubs/${clubId}/import/liquidity`, { method: "POST", body: form, credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(formatApiError(data.detail) || t("Import mislukt")); return; }
      const parts = [t("{n} jaren bijgewerkt").replace("{n}", String((data.imported_years || []).length))];
      if ((data.missing_years || []).length) parts.push(t("{n} ontbrekende jaren").replace("{n}", String(data.missing_years.length)));
      if ((data.unknown_labels || []).length) parts.push(t("{n} onbekende labels").replace("{n}", String(data.unknown_labels.length)));
      toast.success(parts.join(" · "));
      onChange?.();
    } catch {
      toast.error(t("Import mislukt"));
    }
  };

  const btn = "btn-brutal bg-background text-foreground px-3 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2";
  if (!inUse && !revealed) {
    return (
      <div className="mt-6 flex items-center gap-3 flex-wrap text-sm" data-testid="liquidity-add">
        <button type="button" onClick={() => { setRevealed(true); setOpen(true); }} data-testid="liq-add-link"
          className="inline-flex items-center gap-1.5 font-display uppercase font-bold text-xs tracking-wide text-primary hover:underline">
          <Plus className="w-3.5 h-3.5" /> {t("Kasstroom-opbouw toevoegen")}
          <span className="px-1 py-px border border-muted-foreground/60 text-muted-foreground text-[9px] tracking-widest">{t("optioneel")}</span>
        </button>
        <span className="text-xs text-muted-foreground">{t("Verklaart hoe het resultaat uitkomt op de kaspositie. Niet nodig voor scenario's of BTW; de AI-import vult dit mee uit het jaarverslag.")}</span>
      </div>
    );
  }
  return (
    <div className="bg-card border-2 border-foreground heavy-shadow mt-6" data-testid="liquidity-matrix">
      <div className="p-5 flex items-center justify-between gap-3 flex-wrap">
        <button type="button" onClick={() => setOpen((v) => !v)} className="text-left flex items-start gap-3 group" data-testid="liq-toggle" aria-expanded={open}>
          <span className="mt-1 border-2 border-foreground p-0.5 group-hover:bg-muted">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</span>
          <span>
            <span className="stat-label block">{t("Kasstroom-opbouw per boekjaar (indirecte methode)")}</span>
            <span className="font-display font-black text-2xl uppercase tracking-tight block">
              {t("Liquide middelen")}
              <span className="ml-2 align-middle px-1 py-px border border-muted-foreground/60 text-muted-foreground font-sans font-normal normal-case text-[10px] tracking-widest" data-testid="liq-optional-badge">{t("optioneel")}</span>
            </span>
            {!open && (
              <span className="text-xs text-muted-foreground block mt-1" data-testid="liq-summary">
                {!inUse
                  ? t("Nog niet ingevuld — niet nodig voor scenario's of BTW; de AI-import vult dit mee uit het jaarverslag.")
                  : mismatches === 0
                    ? t("Alle jaren sluiten aan op Kas + bank 31/12.")
                    : t("{n} jaar/jaren sluiten nog niet aan op Kas + bank 31/12 — klap uit om de kasstroom te verklaren.").replace("{n}", String(mismatches))}
              </span>
            )}
          </span>
        </button>
        {open && (
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => downloadXlsxFrom(`/clubs/${clubId}/templates/liquidity.xlsx`, "template", t, clubId)} data-testid="liq-template-btn" className={btn}>
              <FileSpreadsheet className="w-4 h-4" /> {t("Sjabloon")}
            </button>
            <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv" className="hidden" data-testid="liq-file-input"
              onChange={(e) => { if (e.target.files?.[0]) { uploadLiquidity(e.target.files[0]); e.target.value = ""; } }} />
            <button onClick={() => fileRef.current?.click()} data-testid="liq-upload-btn" disabled={locked}
              title={locked ? t("Vergrendeld — ontgrendel de voorgaande jaren om te importeren") : t("Importeer de kasstroom-opbouw uit het sjabloon")}
              className={`btn-brutal bg-secondary text-secondary-foreground px-3 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2 ${locked ? "opacity-40 cursor-not-allowed" : ""}`}>
              <Upload className="w-4 h-4" /> {t("Importeren")}
            </button>
            <button onClick={() => downloadXlsxFrom(`/clubs/${clubId}/export/liquidity.xlsx`, "export", t, clubId)} data-testid="liq-export-btn" className={btn}>
              <Download className="w-4 h-4" /> {t("Excel")}
            </button>
          </div>
        )}
      </div>
      {open && (
        <>
          <p className="px-5 pb-4 -mt-2 text-sm text-muted-foreground max-w-4xl">
            {t("Verklaar per boekjaar hoe het exploitatieresultaat uitkomt op de kaspositie per 31 december: start met de liquide middelen op 1 januari, tel het resultaat en de afschrijving erbij en vul de overige kasmutaties in (+ = kastoename, − = kasafname). Onderaan zie je of het berekende saldo aansluit op Kas + bank 31/12.")}
          </p>
          <div className="overflow-x-auto border-t-2 border-foreground">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 border-b-2 border-foreground">
                  <th className="text-left px-3 py-2 stat-label sticky left-0 sticky-tint-50 min-w-[220px] md:min-w-[320px]">{t("Post")}</th>
                  {rows.map((r) => <th key={r.id} className="px-3 py-2 stat-label text-right min-w-[120px]" data-testid={`liq-year-header-${r.year}`}>{r.year}</th>)}
                </tr>
              </thead>
              <tbody>
                {LIQ_ROWS.map((cfg) => (
                  <tr key={cfg.key} className={`border-b border-foreground/10 ${cfg.computed || cfg.opening ? "bg-muted/10" : ""}`} data-testid={`liq-row-${cfg.key}`}>
                    <td className={`px-3 py-1.5 sticky left-0 ${cfg.computed || cfg.opening ? "sticky-tint-10 italic text-muted-foreground" : "bg-card"}`}>{t(cfg.label)}</td>
                    {rows.map((r, i) => {
                      const testid = `liq-cell-${cfg.key}-${r.year}`;
                      if (cfg.opening && i > 0) {
                        const val = num(rows[i - 1]?.cash_bank_balance);
                        return <td key={r.id} className="px-3 py-1.5 font-mono-num text-right text-muted-foreground italic" title={t("Automatisch: Kas + bank 31/12 van het vorige jaar")} data-testid={testid}>{fmtInt(val)}</td>;
                      }
                      if (cfg.editable) {
                        return (
                          <td key={r.id} className="px-1 py-0.5">
                            <MoneyInput value={r[cfg.key] || 0} readOnly={locked} onCommit={(v) => { if (num(v) !== num(r[cfg.key])) saveField(r, cfg.key, v); }} testid={testid}
                              className={`w-full bg-transparent px-2 py-1 font-mono-num text-right focus:outline-none focus:bg-primary/10 ${locked ? "cursor-not-allowed opacity-70" : ""} ${num(r[cfg.key]) === 0 ? "text-muted-foreground/60" : ""}`} />
                          </td>
                        );
                      }
                      return <td key={r.id} className="px-3 py-1.5 font-mono-num text-right text-muted-foreground" data-testid={testid}>{fmtInt(cfg.computed(r))}</td>;
                    })}
                  </tr>
                ))}
                <tr className="border-t-2 border-foreground bg-muted/40" data-testid="liq-row-saldo">
                  <td className="px-3 py-2 sticky left-0 sticky-tint-40 font-display uppercase font-black">{t("Saldo 31 december (berekend)")}</td>
                  {rows.map((r, i) => <td key={r.id} className="px-3 py-2 font-mono-num text-right font-bold" data-testid={`liq-cell-saldo-${r.year}`}>{fmtInt(computeSaldo(rows, i))}</td>)}
                </tr>
                <tr className="border-b border-foreground/10" data-testid="liq-row-cash">
                  <td className="px-3 py-1.5 sticky left-0 bg-card italic text-muted-foreground">{t("Kas + bank 31/12 (ingevuld)")}</td>
                  {rows.map((r) => <td key={r.id} className="px-3 py-1.5 font-mono-num text-right text-muted-foreground" data-testid={`liq-cell-cash-${r.year}`}>{fmtInt(num(r.cash_bank_balance))}</td>)}
                </tr>
                <tr data-testid="liq-row-crosscheck">
                  <td className="px-3 py-2 sticky left-0 bg-card text-xs stat-label">{t("Aansluiting")}</td>
                  {rows.map((r, i) => {
                    const delta = Math.round(computeSaldo(rows, i) - num(r.cash_bank_balance));
                    const ok = Math.abs(delta) <= 1;
                    if (!inUse) {
                      return <td key={r.id} className="px-3 py-2 text-right text-xs text-muted-foreground italic" title={t("Verschijnt zodra je regels invult")} data-testid={`liq-cell-check-${r.year}`}>{t("nog niet ingevuld")}</td>;
                    }
                    return (
                      <td key={r.id} className={`px-3 py-2 font-mono-num text-right text-xs font-bold ${ok ? "text-[#00A047]" : "text-destructive"}`}
                        title={ok ? t("Sluit aan") : t("Verschil berekend − ingevuld")} data-testid={`liq-cell-check-${r.year}`}>
                        {ok ? <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> {t("Sluit aan")}</span>
                          : <span className="inline-flex items-center gap-1"><X className="w-3.5 h-3.5" /> {delta > 0 ? "+" : "−"}{fmtInt(Math.abs(delta))}</span>}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
          <p className="px-5 py-3 text-[11px] text-muted-foreground border-t border-foreground/10">
            {t("De regel BTW afdracht/teruggave is hetzelfde veld als het ijkpunt 'Werkelijke afdracht' in het BTW-blok (daar met omgekeerd teken: + = betaald).")}
          </p>
        </>
      )}
    </div>
  );
}
